import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { PinterestShareButton } from 'react-share';
import SubHead from '../SubHead/SubHead';
import MiniHed from '../SubHead/MiniHed';
import BodyText from '../BodyText/BodyText';
import Button from '../../../common/Button';
import { getFileName, isValid, isText, decodeSpecialCharacters, segmentTrackEvent, getRelValue, relAttr } from '../../../../utils';
import { appendImgixParamsArticle } from '../../../../imgixUtils';
import './image-module.scss';

const getImageDimensionsFromSrc = (src) => {
  if (!isText(src)) {
    return null;
  }

  const dimensions = src.match(/-(\d+)x(\d+)(?=\.[a-z]+(?:\?|$))/i);
  if (!dimensions) {
    return null;
  }

  const width = Number(dimensions[1]);
  const height = Number(dimensions[2]);

  if (!width || !height) {
    return null;
  }

  return { width, height };
};

const getImageReservedSpaceStyle = ({
  isMobile,
  mobileWidth,
  mobileHeight,
  imageSrc,
}) => {
  const dimensionsFromSrc = getImageDimensionsFromSrc(imageSrc);
  const hasMobileDimensions = Number(mobileWidth) > 0 && Number(mobileHeight) > 0;
  const mobileDimensions = hasMobileDimensions
    ? { width: Number(mobileWidth), height: Number(mobileHeight) }
    : null;
  const dimensions = isMobile
    ? mobileDimensions || dimensionsFromSrc
    : dimensionsFromSrc || mobileDimensions;

  if (!dimensions) {
    return undefined;
  }

  return { aspectRatio: `${dimensions.width} / ${dimensions.height}` };
};

const ImageModule = ({
  data, className, isMobile, isGuide, isShoppableStorefront, additionalClassName, fullUrl, articleLayout, isAffiliate, seo,
}) => {
  if (!isValid(data)) { return null; }
  const {
    imageSrcDesktop,
    imageSrcMobile,
    imageUrl,
    imageAlt,
    hed,
    subhed,
    hedBody,
    bodyText,
    sponsored,
    price,
    oldPrice,
    imageMobileCredit,
    imageMobileCreditURL,
    imageMobileCaption,
    imageMobileCaptionURL,
    imageH1Text,
    imageH2Text,
    imageSrcOpacity,
    imageSrcMobileOpacity,
    imageSrcTextColor,
    imageSrcMobileTextColor,
    imageSrcPrefix,
    pinterestImageOverride,
    textPlacement,
    heroImageUrl = null,
    mobileOverrideImage,
    mobileOverrideImageAlt,
    mobileOverrideImageCredit,
    mobileOverrideImageCreditUrl,
    mobileHeight,
    mobileWidth,
  } = data;
  let {
    credit,
    creditUrl,
    caption,
    captionUrl,
    pinterestHide = false,
  } = data;

  // TODO We are not truncating the body text within the multi-image row modules anymore, but this function could be added to utils.js, as could be useful in the future if we want to truncate and add ellipses to text.
  /* Logic for when subhed length is between > x & x chars, truncate bodyText to x chars. Includes logic for edge cases when last character is punctuation, to remove it. */
  const truncate = (input) => {
    let final;
    const regex = /\s*$|\W*$/;
    try {
      final = input.substring(0, 138).replace(regex, '');
      return `${final}...`;
    } catch (e) {
      console.log(e);
      return null
    }
    
  };

  const isStandard = (articleLayout === 'Standard');

  const imageSrc = (isMobile && imageSrcPrefix !== "multi-row" && isValid(imageSrcMobile)) ? imageSrcMobile : isValid(imageSrcDesktop) ? imageSrcDesktop : null;
  const imageUrlRel = getRelValue(imageUrl, true);
  const heroUrlRel = getRelValue(heroImageUrl, true);
  const largeImageSrc = (isMobile && className === 'large-image' && isValid(mobileOverrideImage)) ? mobileOverrideImage : imageSrc;
  const largeImageAlt = (isMobile && className === 'large-image' && largeImageSrc && isValid(mobileOverrideImageAlt)) ? mobileOverrideImageAlt : imageAlt;
  const largeImageCredit = (isMobile && className === 'large-image' && largeImageSrc && isValid(mobileOverrideImageCredit)) ? mobileOverrideImageCredit : null;
  const largeImageCreditUrl = (isMobile && className === 'large-image' && largeImageSrc && isValid(mobileOverrideImageCreditUrl)) ? mobileOverrideImageCreditUrl : null;
  const largeCreditUrlRel = getRelValue(largeImageCreditUrl, true);
  const renderedImage = (imageSrc)  ? <img className={className} src={imageSrc} alt={isText(imageAlt) ? imageAlt : getFileName(imageSrc)} loading={(className === 'hero-image') ? "eager" : "lazy"} fetchpriority={(className === 'hero-image') ? "high" : "auto"}/> : null;
  const largeRenderedImage = (largeImageSrc) ? <img className={className} src={largeImageSrc} alt={(isText(largeImageAlt)) ? largeImageAlt : getFileName(largeImageSrc)} loading="lazy"/> : null;
  const { buttonText, buttonUrl } = data;
  const button = (isText(buttonText) && isText(buttonUrl)) ? {text: buttonText, url: buttonUrl} : null;
  const renderedButton = (button !== null) ? <Button button={button} /> : null;
  const renderedOldPrice = isText(oldPrice) ? <span className='old-price'><span>{oldPrice}</span>,&nbsp;</span> : '';
  const renderedPrice = isText(price) ? <div className='body-text'>{renderedOldPrice}{price}</div> : null;
  const pinMedia = isValid(pinterestImageOverride) ? pinterestImageOverride : imageSrc;
  const showPin = Boolean(fullUrl && pinMedia);
  const isList = (articleLayout === 'Standard');
  const placementClass = isValid(textPlacement) ? textPlacement : 'below';
  let hedBelow = (textPlacement === 'below');
  if (className === 'teaser-image') {
    hedBelow = true;
  }
  // Checking if hedBody exists, if nothing entered in Editor, comes through as this string on line 79. If that string or empty or undefined, we don't want to render it bc it adds extra spacing where we don't want it.
  const noHedBody = (isValid(hedBody) && (hedBody.includes('&lt;p&gt;&lt;/p&gt;')));
  const emptyHedBody = noHedBody || (!isValid(hedBody));
  const parsedHedBody = emptyHedBody ? '' : hedBody;
  // Lines 100-110: If articleLayout = "Guide", and REST has values for Hero Image h1 and h2 Text, display it along with the chosen text color and overlay opacity (chosen in the Editor) on top of the hero image
  const heroImgTextColor = (isMobile && isValid(imageSrcMobileTextColor)) ? imageSrcMobileTextColor : isValid(imageSrcTextColor) ? imageSrcTextColor : '#000000';
  const heroImgOverlayOpacity = (isMobile && isValid(imageSrcMobileOpacity)) ? imageSrcMobileOpacity : isValid(imageSrcOpacity) ? imageSrcOpacity : 0;
  // styling will need positioning to overlay on top of image as well
  const heroOverlayTextStyle = { color: heroImgTextColor };
  const renderedHeroImgPrincipal = (isText(imageH1Text))
  ? <h2 className="hero-overlay-txt principal" style={heroOverlayTextStyle}>{imageH1Text}</h2> : null;
  const renderedHeroImgH2 = (isText(imageH2Text))
  ? <h2 className="hero-overlay-txt h2" style={heroOverlayTextStyle}>{imageH2Text}</h2> : null;

  credit = (isValid(imageMobileCredit) && isMobile) ? imageMobileCredit : isValid(credit) ? credit : null;
  caption = (isValid(imageMobileCaption) && isMobile) ? imageMobileCaption : isValid(caption) ?  caption : null;
  creditUrl = (isValid(imageMobileCreditURL) && isMobile) ? imageMobileCreditURL : isValid(creditUrl) ? creditUrl : null;
  captionUrl = (isValid(imageMobileCaptionURL) && isMobile) ? imageMobileCaptionURL : isValid(captionUrl) ? captionUrl : null;
  const captionUrlRel = getRelValue(captionUrl, true) ;
  const creditUrlRel = getRelValue(creditUrl, true);

  const containerClassName = additionalClassName ? `${className}-container ${additionalClassName} ${placementClass}` : `${className}-container ${placementClass}`;

  const imageWrapperClassName = (isGuide || isShoppableStorefront) ? 'guide-hero-image-wrapper' : 'image-wrapper';
  const imageReservedSpaceStyle = getImageReservedSpaceStyle({
    isMobile,
    mobileWidth,
    mobileHeight,
    imageSrc: largeImageSrc || imageSrc,
  });

  if (!renderedImage || !showPin) {
    pinterestHide = true;
  }

  return (
    <div className={containerClassName}>
      <div className={imageWrapperClassName}>
        {/* If header and headerbody exist, and textPlacement is NOT 'below', render them above the image */}
        {(isText(hed) && !hedBelow)
          && <SubHead className={isStandard ? 'sub-head standard image-head' : 'sub-head image-head'} data={hed}/>
        }
        {(isText(parsedHedBody) && !hedBelow)
          && <BodyText className={isStandard ? 'header-body standard' : 'header-body'} data={parsedHedBody} articleLayout={articleLayout} isAffiliate={isAffiliate}/>
        }
        {/* render image (linked or not) and pinterest pin icon + sponsored text */}
        {(isText(imageUrl) && (imageSrc !== null)) &&
          <a className="large-image-link" href={decodeSpecialCharacters(imageUrl)} target="_blank" {...relAttr(imageUrlRel)} data-link-type="image-link" >
            <div 
              className={`img-container-${className}`}
                style={imageReservedSpaceStyle}>
                <a href={decodeSpecialCharacters(imageUrl)} target="_blank" {...relAttr(imageUrlRel)} data-link-type="image-link">
                {largeRenderedImage ? largeRenderedImage : renderedImage}
              </a>
              { (!pinterestHide && !isGuide && !isShoppableStorefront)
                && <div className="social-pin-wrapper">
                    <PinterestShareButton
                      className="social-share-unit pinterest"
                      onClick={() => segmentTrackEvent('pinterest_share', {action: 'social', label: 'pin_share'})}
                      url={fullUrl}
                      alt={imageAlt || 'pinterest share image'}
                      media={imageSrc}
                      description={seo.metaDescription}
                      >
                    </PinterestShareButton>
                  </div>
              }
              {sponsored && <span className="sponsored-txt">Sponsored</span>}
            </div>
          </a>
        } 
        {!isText(imageUrl) && (imageSrc !== null) &&
          <div className={`img-container-${className}`}
            style={className !== "hero-image" ? imageReservedSpaceStyle : undefined}>
            { isValid(heroImageUrl) ? (
              <a href={decodeSpecialCharacters(heroImageUrl)} target="_blank" {...relAttr(heroUrlRel)} data-link-type="image-link">
                {renderedImage}
              </a>
            ) : (
              <Fragment>
                {renderedImage}
              </Fragment>
            )} 
            { (!pinterestHide && !isGuide && !isShoppableStorefront)
                && <div className="social-pin-wrapper">
                    <PinterestShareButton
                      className="social-share-unit pinterest"
                      onClick={() => segmentTrackEvent('pinterest_share', {action: 'social', label: 'pin_share'})}
                      url={fullUrl}
                      alt={imageAlt || 'pinterest share image'}
                      media={imageSrc}
                      description={seo.metaDescription}
                      >
                    </PinterestShareButton>
                  </div>
            }
            {sponsored && <span className="sponsored-txt">Sponsored</span>}
          </div>
        }
        {/* Hero Image overlay text (only render if articleLayout is Guide) */}
        {isGuide &&
          <>
            <div className='guide-hero-overlay-wrapper' style={{ opacity: heroImgOverlayOpacity }}></div>
            {renderedHeroImgPrincipal}
            {renderedHeroImgH2}
          </>
        }
        {/* image caption/credit for all image modules minus multi-image row container */}
        {(containerClassName !== 'multi-image-container below' && containerClassName !== 'hero-image-container below' && !isGuide && !isShoppableStorefront) && (
          <div className="img-credit-container">
            {/* img caption */}
            {captionUrl ? (
              <a href={captionUrl} className="caption" target="_blank" {...relAttr(captionUrlRel)} data-link-type="caption-link">{caption}</a>
            ) : (
              <span className="caption">{caption}</span>
            )}
            {((caption && caption !== null && caption !== '') && (credit && credit !== null && credit !== '')) && <span className="img-credit"> / </span>}
            {/* img credit */}
            {creditUrl ? (
              <a href={largeImageCreditUrl ? decodeSpecialCharacters(largeImageCreditUrl) : decodeSpecialCharacters(creditUrl)} className="img-credit" target="_blank" {...relAttr(largeImageCreditUrl ? largeCreditUrlRel : creditUrlRel)} data-link-type="credit-link" dangerouslySetInnerHTML={ { __html: largeImageCredit ? largeImageCredit : credit } }></a>
            ) : (
              <span className="img-credit" dangerouslySetInnerHTML={ { __html: largeImageCredit ? largeImageCredit : credit } }></span>
            )}
          </div>
        )}
        {/* image caption/credit for all hero-image-container */}
        {(containerClassName === 'hero-image-container below' && !isGuide && !isShoppableStorefront) && (
          <div className="img-credit-container hero-credit-container">
            {/* img caption */}
            {captionUrl ? (
              <a href={captionUrl} className="caption" target="_blank" {...relAttr(captionUrlRel)} data-link-type="caption-link">{caption}</a>
            ) : (
              <span className="caption">{caption}</span>
            )}
            {((caption && caption !== null && caption !== '') && (credit && credit !== null && credit !== '')) && <span className="img-credit"> / </span>}
            {/* img credit */}
            {creditUrl ? (
              <a href={decodeSpecialCharacters(creditUrl)} className="img-credit" target="_blank" {...relAttr(creditUrlRel)} data-link-type="credit-link" dangerouslySetInnerHTML={ { __html: credit } }></a>
            ) : (
              <span className="img-credit" dangerouslySetInnerHTML={ { __html: credit } }></span>
            )}
          </div>
        )}
        {/* image caption/credit for hero-image-container and articleLayout is Guide */}
        {(imageWrapperClassName === 'guide-hero-image-wrapper' && (isGuide || isShoppableStorefront)) && (
          <div className="img-credit-container guide-credit-container">
            {/* img caption */}
            {captionUrl ? (
              <a href={captionUrl} className="caption" target="_blank" {...relAttr(captionUrlRel)} data-link-type="caption-link">{caption}</a>
            ) : (
              <span className="caption">{caption}</span>
            )}
            {((caption && caption !== null && caption !== '') && (credit && credit !== null && credit !== '')) && <span className="img-credit"> / </span>}
            {/* img credit */}
            {creditUrl ? (
              <a href={decodeSpecialCharacters(creditUrl)} className="img-credit" target="_blank" {...relAttr(creditUrlRel)} data-link-type="credit-link">{credit}</a>
            ) : (
              <span className="img-credit" dangerouslySetInnerHTML={ { __html: credit } }></span>
            )}
          </div>
        )}
        {/* image credit & sponsored text for multi-image row container */}
        {(containerClassName === 'multi-image-container below') && (
          <div className="img-credit-container-multi-image">
            {creditUrl ? (
              <a href={decodeSpecialCharacters(creditUrl)} className="img-credit-multi-image" target="_blank" {...relAttr(creditUrlRel)} data-link-type="credit-link">{credit}</a>
            ) : (
              <span className="img-credit-multi-image" dangerouslySetInnerHTML={ { __html: credit } }></span>
            )}
          </div>
        )}
      </div>

      <div className={isStandard ? 'image-text-wrapper standard' : 'image-text-wrapper'}>
        {/* if multi-image row module, show sponsored tag here */}
        {((containerClassName === 'multi-image-container below') && sponsored)
          && <span className="sponsored-txt-multi-img">Sponsored</span>}
        {/* If header and headerbody exist, and textPlacement is 'below', render them below the image */}
        {(isText(hed) && hedBelow)
          && <SubHead className={isStandard ? 'sub-head standard image-head' : 'sub-head image-head'} articleLayout={articleLayout} data={hed}/>
        }
        {(isText(parsedHedBody) && hedBelow)
          && <BodyText className={isStandard ? 'header-body standard' : 'header-body'} articleLayout={articleLayout} data={parsedHedBody} isAffiliate={isAffiliate}/>
        }
        {/* This is an h3, aka subhead in the Editor */}
        {isText(subhed) &&
          <MiniHed className={isStandard ? 'sub-head standard' : 'sub-head'} data={subhed} isSuperlative={false}/>
        }
        {/* show body text if hedBelow */}
        {(isValid(containerClassName) && containerClassName.includes('large-image-container'))
          && <BodyText className={isStandard ? 'body-text standard' : 'body-text'} articleLayout={articleLayout} data={bodyText} isAffiliate={isAffiliate}/>
        }
        {/* NOT USING AUTOMATIC BTNS ANYMORE - Editor will add in body-text module - If articleLayout = 'Standard' and textPlacement 'below', render the button under the body-copy */}
        {/* {(isList && hedBelow)
          && <div className="large-image-button">{renderedButton}</div>
        } */}
        {/* NOT USING AUTOMATIC BTNS ANYMORE - Editor will add in body-text module - If articleLayout = 'Guide' and textPlacement 'above', render the button after the body text */}
        {/* {(articleLayout === 'Guide' && textPlacement === 'above')
          && <div className="large-image-button">{renderedButton}</div>
        } */}
        {/* Only show horizontal rule if part of a multi-image row container */}
        {(isValid(containerClassName) && containerClassName === 'multi-image-container below')
          && <>
              {subhed && <hr className="rule"></hr>}
              <BodyText className="body-text" data={bodyText} articleLayout={articleLayout} isAffiliate={isAffiliate}/>
            </>
        }
        {(isValid(containerClassName) && (containerClassName !== 'multi-image-container below')
          && (containerClassName !== 'large-image-container below')
          && (containerClassName !== 'large-image-container above'))
          && (
          <>
            <BodyText className="body-text" data={bodyText} articleLayout={articleLayout} isAffiliate={isAffiliate}/>
            {renderedPrice}
            {renderedButton}
          </>
          )}
      </div>
      {/* Button for multi-image row container */}
      <div className="multi-image-btn-wrapper">
        {(isValid(containerClassName) && containerClassName === 'multi-image-container below')
          && <div className="button-wrapper">
              {renderedButton}
             </div>
        }
      </div>
    </div>
  );
};

ImageModule.propTypes = {
  data: PropTypes.object,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

export default ImageModule;
